import { useState, useEffect, useRef, useMemo } from "react";
import { Slider } from "@mui/material";
import "./TimeLineBar.css";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import { useAccessRights } from "../../../hooks/userPermissions/useAccessRights";
import { getForecastLimit } from "../../../utils/weatherTimelineLimits";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const TIMELINE_WINDOW_HOURS = 24;

const TimelineBar = ({
  onChangeTime,
  onChangeSliderIndex,
  sliderIndex,
  currentTimeZone,
  weatherRequestStartHour,
  weatherRequestDate,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef(null);
  const { getTimelineLimits } = useAccessRights();

  const timelineLimits = getTimelineLimits("weather");
  const hasTimelineLimits = timelineLimits !== null;
  const intervalMaxJ = timelineLimits?.intervalMaxJ ?? null;

  const baseHour = parseInt(weatherRequestStartHour, 10);

  // Limit the 24h window to the permission-based forecast limit.
  // getForecastLimit returns the LAST VALID forecast hour (limit - 1h),
  // so both endpoints are includable: +1 counts both ends inclusively.
  const { TOTAL_HOURS, isTimelineDisabled } = useMemo(() => {
    if (!weatherRequestDate || !hasTimelineLimits || intervalMaxJ === null) {
      return { TOTAL_HOURS: TIMELINE_WINDOW_HOURS, isTimelineDisabled: false };
    }

    const forecastLimit = getForecastLimit(intervalMaxJ);

    const startDateTime = new Date(
      parseInt(weatherRequestDate.year, 10),
      parseInt(weatherRequestDate.month, 10) - 1,
      parseInt(weatherRequestDate.day, 10),
      baseHour,
    );

    const diffHours = Math.floor(
      (forecastLimit.getTime() - startDateTime.getTime()) / (1000 * 60 * 60),
    );

    // +1 because forecastLimit IS the last valid step (inclusive on both ends)
    const permittedPoints = diffHours + 1;
    const total = Math.max(0, Math.min(TIMELINE_WINDOW_HOURS, permittedPoints));

    // total <= 1 means there's nothing meaningful to scrub through
    // (either the limit is before the start, or start === limit exactly).
    return { TOTAL_HOURS: total, isTimelineDisabled: total <= 1 };
  }, [weatherRequestDate, baseHour, hasTimelineLimits, intervalMaxJ]);

  const [displayIndex, setDisplayIndex] = useState(sliderIndex);

  useEffect(() => {
    setDisplayIndex(sliderIndex);
  }, [sliderIndex]);

  useEffect(() => {
    if (sliderIndex > TOTAL_HOURS - 1) {
      onChangeSliderIndex(Math.max(0, TOTAL_HOURS - 1));
    }
  }, [sliderIndex, TOTAL_HOURS, onChangeSliderIndex]);

  useEffect(() => {
    if (isTimelineDisabled && isPlaying) {
      clearInterval(intervalRef.current);
      setIsPlaying(false);
    }
  }, [isTimelineDisabled, isPlaying]);

  const getTimeAtIndex = (index) => {
    const totalHours = baseHour + index;
    const hour = totalHours % 24;
    const daysOffset = Math.floor(totalHours / 24);

    const base = new Date(
      parseInt(weatherRequestDate.year, 10),
      parseInt(weatherRequestDate.month, 10) - 1,
      parseInt(weatherRequestDate.day, 10),
    );
    base.setDate(base.getDate() + daysOffset);

    return {
      hour,
      day: base.getDate().toString().padStart(2, "0"),
      month: (base.getMonth() + 1).toString().padStart(2, "0"),
      year: base.getFullYear().toString(),
    };
  };

  const formatHour = (hour) => `${hour.toString().padStart(2, "0")}:00`;

  const handleSliderChange = (_, value) => setDisplayIndex(value);
  const handleSliderChangeCommitted = (_, value) => onChangeSliderIndex(value);

  const togglePlay = () => {
    if (isTimelineDisabled) return;

    if (isPlaying) {
      clearInterval(intervalRef.current);
    } else {
      intervalRef.current = setInterval(() => {
        onChangeSliderIndex((prev) =>
          prev + 1 > TOTAL_HOURS - 1 ? 0 : prev + 1,
        );
      }, 5000);
    }
    setIsPlaying((prev) => !prev);
  };

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const marks = !isTimelineDisabled
    ? Array.from({ length: TOTAL_HOURS }, (_, i) => {
        const { hour, day, month } = getTimeAtIndex(i);
        if (hour % 6 !== 0) return null;
        return {
          value: i,
          label: hour === 0 ? `${day}/${month}` : formatHour(hour),
        };
      }).filter(Boolean)
    : [];

  const current = weatherRequestDate
    ? getTimeAtIndex(displayIndex)
    : { day: "--", month: "--", year: "----", hour: 0 };

  return (
    <div className="timeBar-comp" data-testid="timeline-bar">
      <div className="timebar-header">
        <div>
          <span
            className="time-label-timeline"
            data-testid="timeline-current-time-label"
          >
            {current.day}/{current.month}/{current.year}{" "}
            {formatHour(current.hour)}{" "}
            {currentTimeZone === "UTC+1" ? "CET" : "CEST"}
          </span>
        </div>
        <div
          onClick={togglePlay}
          style={{ cursor: isTimelineDisabled ? "not-allowed" : "pointer" }}
          data-testid="timeline-play-pause-button"
          aria-disabled={isTimelineDisabled}
        >
          {isPlaying ? (
            <PauseCircleIcon
              className="timeline-icon"
              style={{
                fontSize: "30px",
                opacity: isTimelineDisabled ? 0.4 : 1,
              }}
              data-testid="timeline-pause-icon"
            />
          ) : (
            <PlayCircleFilledWhiteIcon
              className="timeline-icon"
              style={{
                fontSize: "30px",
                opacity: isTimelineDisabled ? 0.4 : 1,
              }}
              data-testid="timeline-play-icon"
            />
          )}
        </div>
      </div>
      {!isTimelineDisabled ? (
        <div
          style={{ display: "flex", alignItems: "center" }}
          data-testid="timeline-slider-container"
        >
          <Slider
            value={displayIndex}
            min={0}
            max={TOTAL_HOURS - 1}
            step={1}
            marks={marks}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            className="slider-timeline"
            data-testid="timeline-slider"
          />
        </div>
      ) : (
        <div
          className="timeline-unavailable-banner"
          data-testid="timeline-unavailable-message"
        >
          <WarningAmberIcon className="timeline-unavailable-icon" />
          <span>
            Vous ne pouvez pas dépasser cette date et heure selon vos droits
            d'accès.
          </span>
        </div>
      )}
    </div>
  );
};

export default TimelineBar;